import {
  FormInput,
  PaymentProcessorEnum,
  PrimaryButton,
  getPaymentProcessorTitle,
} from '@systemeio/ui'
import HelpCenterLink from 'modules/profile/payment-gateways/components/help-center-link'
import { useConnectedPaymentIntegrations } from 'modules/profile/payment-gateways/hooks/use-connected-payment-integrations'
import { useCreateXenditIntegration } from 'modules/profile/payment-gateways/integrations/xendit/hooks'
import {
  XenditCreateIntegrationInterface,
  XenditIntegrationErrorsInterface,
} from 'modules/profile/payment-gateways/integrations/xendit/types'
import { getXenditHelpPageLink } from 'modules/profile/payment-gateways/integrations/xendit/utils'
import React, { useState } from 'react'
import CreateModal from 'shared/components/create-modal'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import useUser from 'shared/hooks/use-user'
import { fetchWithErrors } from 'shared/utils/fetch-with-errors'

const defaultValues: XenditCreateIntegrationInterface = {
  organizationName: '',
  webhookSecret: '',
  secretAccessToken: '',
  publicAccessToken: '',
}

const defaultErrors: XenditIntegrationErrorsInterface = {
  organizationName: '',
  webhookSecret: '',
  secretAccessToken: '',
  publicAccessToken: '',
}

const XenditCreateModal = () => {
  const { t } = useLocoTranslation()
  const { user } = useUser()
  const [opened, setOpened] = useState(false)
  const [errors, setErrors] = useState(defaultErrors)
  const { createXenditIntegration } = useCreateXenditIntegration()

  const { mutatePaymentIntegrations } = useConnectedPaymentIntegrations()

  const onCreate = async (data: XenditCreateIntegrationInterface) => {
    await fetchWithErrors(async () => {
      await createXenditIntegration(data)
      await mutatePaymentIntegrations()
    }, setErrors)
  }

  return (
    <>
      <PrimaryButton
        onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
          e.preventDefault()
          setOpened(true)
        }}
        className={'font-bold'}
      >
        {t('dashboard.settings.payment_gateways.connect')}
      </PrimaryButton>
      <CreateModal
        fullCaption={t('dashboard.settings.payment_gateways.connect_payment_title', {
          payment: getPaymentProcessorTitle(PaymentProcessorEnum.XenditID),
        })}
        caption={t('dashboard.settings.payment-gateways.xendit_integration')}
        opened={opened}
        onClose={() => setOpened(false)}
        defaultValues={defaultValues}
        needToast={false}
        creatable={[
          {
            field: 'organizationName',
            onRender: (value, onChange) => (
              <FormInput
                key={'xendit-organization-name-create'}
                label={t('dashboard.settings.payment-gateways.organization_name')}
                value={value}
                onChange={e => onChange(e.target.value)}
                required
                error={errors.organizationName}
              />
            ),
          },
          {
            field: 'webhookSecret',
            onRender: (value, onChange) => (
              <FormInput
                key={'xendit-webhook-secret-create'}
                label={t('dashboard.settings.payment-gateways.webhook_secret')}
                value={value}
                onChange={e => onChange(e.target.value)}
                required
                error={errors.webhookSecret}
              />
            ),
          },
          {
            field: 'secretAccessToken',
            onRender: (value, onChange) => (
              <FormInput
                key={'xendit-secret-access-token-create'}
                label={t('dashboard.settings.payment-gateways.secret_access_token')}
                value={value}
                onChange={e => onChange(e.target.value)}
                required
                error={errors.secretAccessToken}
              />
            ),
          },
          {
            field: 'publicAccessToken',
            onRender: (value, onChange) => (
              <FormInput
                key={'xendit-public-access-token-create'}
                label={t('dashboard.settings.payment-gateways.public_access_token')}
                value={value}
                onChange={e => onChange(e.target.value)}
                required
                error={errors.publicAccessToken}
              />
            ),
          },
        ]}
        additionalInfo={
          <HelpCenterLink
            link={getXenditHelpPageLink(user?.dashboardLocale)}
            integration={getPaymentProcessorTitle(PaymentProcessorEnum.XenditPH)}
          />
        }
        onCreate={onCreate}
        requiredFields={[
          'organizationName',
          'webhookSecret',
          'secretAccessToken',
          'publicAccessToken',
        ]}
      />
    </>
  )
}

export default XenditCreateModal
